import React, { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Upload,
  File,
  Trash2,
  Download,
  Search,
  FileText,
  Image,
  FileVideo,
  FileAudio,
  AlertCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useDocuments } from "@/hooks/useDocuments";

const Documentos = () => {
  const {
    documents,
    loading,
    uploading,
    loadDocuments,
    uploadDocument,
    uploadMultipleDocuments,
    deleteDocument,
    downloadDocument,
  } = useDocuments();

  const [searchTerm, setSearchTerm] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [description, setDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(
    new Set()
  );
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // Filtrar documentos por busca
  const filteredDocuments = documents.filter(
    (doc) =>
      doc.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Função para formatar tamanho do arquivo
  const formatFileSize = (bytes: number | null | undefined) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Função para obter ícone do arquivo
  const getFileIcon = (contentType: string | null | undefined) => {
    if (!contentType) return <FileText className="h-4 w-4" />;
    if (contentType.startsWith("image/")) return <Image className="h-4 w-4" />;
    if (contentType.startsWith("video/"))
      return <FileVideo className="h-4 w-4" />;
    if (contentType.startsWith("audio/"))
      return <FileAudio className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  // Função para formatar data
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Data não disponível";
    try {
      return new Date(dateString).toLocaleDateString("pt-BR");
    } catch {
      return "Data inválida";
    }
  };

  // Função para upload de arquivos
  const handleFileUpload = useCallback(
    async (files: FileList, desc?: string) => {
      if (desc) {
        // Upload com descrição individual para cada arquivo
        for (let i = 0; i < files.length; i++) {
          await uploadDocument(files[i], desc);
        }
      } else {
        await uploadMultipleDocuments(files);
      }
      // Limpar campos após upload
      setDescription("");
      setSelectedFiles(null);
    },
    [uploadMultipleDocuments, uploadDocument]
  );

  // Função para confirmar upload com descrição
  const handleConfirmUpload = useCallback(() => {
    if (selectedFiles) {
      handleFileUpload(selectedFiles, description.trim() || undefined);
    }
  }, [selectedFiles, description, handleFileUpload]);

  // Handlers para drag and drop
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFiles(e.dataTransfer.files);
    }
  }, []);

  // Função para seleção de arquivos
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (files && files.length > 0) {
      setSelectedFiles(files);
    }
  }, []);

  // Função para deletar documento
  const handleDelete = async (id: string) => {
    await deleteDocument(id);
  };

  // Função para download
  const handleDownload = async (doc: any) => {
    await downloadDocument(doc);
  };

  // Funções de seleção múltipla
  const toggleDocumentSelection = (docId: string) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocuments(newSelected);
  };

  const selectAllDocuments = () => {
    const allIds = new Set(filteredDocuments.map((doc) => doc.id));
    setSelectedDocuments(allIds);
  };

  const clearSelection = () => {
    setSelectedDocuments(new Set());
    setIsSelectionMode(false);
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.size === 0) return;

    const confirmed = window.confirm(
      `Tem certeza que deseja excluir ${selectedDocuments.size} documento(s)?`
    );
    if (!confirmed) return;

    try {
      for (const docId of selectedDocuments) {
        await deleteDocument(docId);
      }
      clearSelection();
    } catch (error) {
      console.error("Erro ao excluir documentos:", error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <File className="h-8 w-8" />
          Documentos
        </h1>
        <p className="text-gray-600 mt-2">
          Gerencie seus documentos e arquivos
        </p>
      </div>

      {/* Área de Upload */}
      <Card>
        <CardHeader>
          <CardTitle>Upload de Documentos</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              dragActive ? "border-primary bg-primary/5" : "border-gray-300",
              uploading && "opacity-50 pointer-events-none"
            )}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  Enviando arquivos...
                </p>
              </div>
            ) : (
              <>
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  Arraste arquivos aqui ou clique para selecionar
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Suporte para PDF e documentos (máximo 10MB)
                </p>
                <Input
                  type="file"
                  multiple
                  className="hidden"
                  id="file-upload"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
                <Button asChild>
                  <label htmlFor="file-upload" className="cursor-pointer">
                    Selecionar Arquivos
                  </label>
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Área de Confirmação de Upload */}
      {selectedFiles && selectedFiles.length > 0 && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-primary">Confirmar Upload</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                {selectedFiles.length} arquivo(s) selecionado(s):
              </p>
              <div className="space-y-1">
                {Array.from(selectedFiles).map((file, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <File className="h-4 w-4" />
                    <span>{file.name}</span>
                    <span className="text-muted-foreground">
                      ({formatFileSize(file.size)})
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Descrição (opcional)
              </label>
              <Input
                placeholder="Adicione uma descrição para os arquivos..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={255}
              />
              <p className="text-xs text-muted-foreground">
                A mesma descrição será aplicada a todos os arquivos selecionados
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleConfirmUpload}
                disabled={uploading}
                className="flex-1"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Enviar Arquivos
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedFiles(null);
                  setDescription("");
                }}
                disabled={uploading}
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Documentos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle>Meus Documentos ({documents.length})</CardTitle>
              {selectedDocuments.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedDocuments.size} selecionado(s)
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Excluir
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearSelection}>
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isSelectionMode ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSelectionMode(true)}
                  disabled={filteredDocuments.length === 0}
                >
                  Selecionar
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllDocuments}
                >
                  Selecionar Todos
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={loadDocuments}
                disabled={loading}
              >
                <RefreshCw
                  className={cn("h-4 w-4", loading && "animate-spin")}
                />
                Atualizar
              </Button>
              <div className="relative w-64">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar documentos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-gray-500">Carregando documentos...</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-8">
              <File className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {documents.length === 0
                  ? "Nenhum documento enviado ainda"
                  : "Nenhum documento encontrado"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className={cn(
                    "flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors",
                    selectedDocuments.has(doc.id) &&
                      "bg-primary/5 border-primary/20"
                  )}
                >
                  <div className="flex items-center gap-3">
                    {isSelectionMode && (
                      <Checkbox
                        checked={selectedDocuments.has(doc.id)}
                        onCheckedChange={() => toggleDocumentSelection(doc.id)}
                      />
                    )}
                    {getFileIcon(doc.contentType)}
                    <div>
                      <h4 className="font-medium">
                        {doc.originalName || "Nome não disponível"}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(doc.fileSize)} •{" "}
                        {formatDate(doc.uploadedAt)}
                        {doc.description && ` • ${doc.description}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {doc.contentType?.split("/")[0] || "arquivo"}
                    </Badge>
                    {!isSelectionMode && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(doc)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(doc.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Estatísticas */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">
                {documents.length}
              </div>
              <p className="text-sm text-muted-foreground">
                {documents.length === 1 ? "Documento" : "Documentos"}
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">
                {(() => {
                  const totalUsed = documents.reduce(
                    (total, doc) => total + (doc.fileSize || 0),
                    0
                  );
                  const maxLimit = 25 * 1024 * 1024; // 25MB em bytes
                  return `${formatFileSize(totalUsed)} / ${formatFileSize(
                    maxLimit
                  )}`;
                })()}
              </div>
              <p className="text-sm text-muted-foreground">Espaço Usado</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">
                {(() => {
                  const totalUsed = documents.reduce(
                    (total, doc) => total + (doc.fileSize || 0),
                    0
                  );
                  const maxLimit = 25 * 1024 * 1024; // 25MB em bytes
                  const percentage =
                    maxLimit > 0 ? Math.round((totalUsed / maxLimit) * 100) : 0;
                  return `${percentage}%`;
                })()}
              </div>
              <p className="text-sm text-muted-foreground">Limite Usado</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Informações */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Dica:</strong> Você pode arrastar múltiplos arquivos
          diretamente para a área de upload. Formatos suportados: PDF, DOC,
          DOCX, XLS, XLSX, PPT, PPTX.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default Documentos;
